import { useMemo, useCallback, useState } from 'react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useOpenAIStatus, useGenerateCaption } from '@/api/hooks/useOpenAI'
import { Sparkles, Loader2, Copy, ClipboardPaste } from 'lucide-react'
import { toast } from 'sonner'
import type { Account } from '@/types'
import type { WizardState, WizardAction, CaptionOverride } from './types'
import { parseCaptionList } from './utils'

interface Props {
  state: WizardState
  dispatch: React.Dispatch<WizardAction>
  accounts: Account[]
}

export function WizardStep2({ state, dispatch, accounts }: Props) {
  const { data: aiStatus } = useOpenAIStatus()
  const generateCaption = useGenerateCaption()

  const hasIG = useMemo(() => Object.values(state.selectedAccounts).some(v => v.ig), [state.selectedAccounts])
  const hasFB = useMemo(() => Object.values(state.selectedAccounts).some(v => v.fb), [state.selectedAccounts])
  const hasYT = useMemo(() => Object.values(state.selectedYtChannels).some(v => v), [state.selectedYtChannels])
  const showCaption = hasIG || hasFB
  const showYT = hasYT

  const firstSelectedIgUserId = useMemo(() => {
    for (const [igUserId, p] of Object.entries(state.selectedAccounts)) {
      if (p.ig || p.fb) return igUserId
    }
    return null
  }, [state.selectedAccounts])

  // Get resolved value for an item (override ?? global)
  const getCaption = (itemId: string) => state.overrides[itemId]?.caption ?? state.globalCaption
  const getYtTitle = (itemId: string) => state.overrides[itemId]?.ytTitle ?? state.globalYtTitle
  const getYtDesc = (itemId: string) => state.overrides[itemId]?.ytDescription ?? state.globalYtDescription

  const updateItemField = useCallback((itemId: string, field: 'caption' | 'ytTitle' | 'ytDescription', value: string) => {
    const existing = state.overrides[itemId] || {}
    dispatch({ type: 'SET_OVERRIDE', itemId, override: { ...existing, [field]: value } })
  }, [state.overrides, dispatch])

  // Copy first item's value to all others
  const applyFirstToAll = (field: 'caption' | 'ytTitle' | 'ytDescription') => {
    if (state.items.length < 2) return
    const firstId = state.items[0].id
    const val = getFieldValue(firstId, field)
    for (let i = 1; i < state.items.length; i++) {
      const id = state.items[i].id
      const existing = state.overrides[id] || {}
      dispatch({ type: 'SET_OVERRIDE', itemId: id, override: { ...existing, [field]: val } })
    }
    toast.success(`Aplicado a ${state.items.length - 1} itens`)
  }

  const getFieldValue = (itemId: string, field: 'caption' | 'ytTitle' | 'ytDescription') => {
    if (field === 'caption') return getCaption(itemId)
    if (field === 'ytTitle') return getYtTitle(itemId)
    return getYtDesc(itemId)
  }

  const handleAI = async (itemId: string) => {
    if (!firstSelectedIgUserId) { toast.error('Nenhuma conta IG/FB selecionada'); return }
    const item = state.items.find(i => i.id === itemId)
    try {
      const { caption } = await generateCaption.mutateAsync({
        igUserId: firstSelectedIgUserId,
        videoTitle: item?.filename,
      })
      updateItemField(itemId, 'caption', caption)
      toast.success('Legenda gerada')
    } catch (err) {
      toast.error((err as Error).message || 'Erro ao gerar legenda')
    }
  }

  const copyAllCaptions = () => {
    const text = state.items.map((it, i) => `${i + 1}. ${getCaption(it.id)}`).join('\n')
    navigator.clipboard.writeText(text)
    toast.success(`${state.items.length} legendas copiadas`)
  }

  const copyAllFilenames = () => {
    const text = state.items.map((it, i) => `${i + 1}. ${it.filename.replace(/\.[^.]+$/, '')}`).join('\n')
    navigator.clipboard.writeText(text)
    toast.success(`${state.items.length} titulos copiados`)
  }

  // ── Colar legendas em massa: linha i → video i (contraparte do "Copiar legendas") ──
  const [pasteOpen, setPasteOpen] = useState(false)
  const [pasteText, setPasteText] = useState('')
  const pasteCount = useMemo(() => parseCaptionList(pasteText).length, [pasteText])

  // Grava uma legenda por item, na ordem, e deixa um "Desfazer" no aviso com o retrato anterior.
  const applyCaptionList = (list: string[]) => {
    const n = Math.min(list.length, state.items.length)
    if (n === 0) return
    const before: Record<string, CaptionOverride> = {}
    const after: Record<string, CaptionOverride> = {}
    for (let i = 0; i < n; i++) {
      const id = state.items[i].id
      const existing = state.overrides[id] || {}
      before[id] = { ...existing }
      after[id] = { ...existing, caption: list[i] }
    }
    dispatch({ type: 'SET_OVERRIDES_BULK', overrides: after })
    const missing = state.items.length - n
    const extra = list.length - n
    const detail = missing > 0 ? `, faltaram ${missing}` : extra > 0 ? `, ${extra} linhas ignoradas` : ''
    toast.success(`${n} legendas coladas${detail}`, {
      action: { label: 'Desfazer', onClick: () => dispatch({ type: 'SET_OVERRIDES_BULK', overrides: before }) },
    })
    setPasteOpen(false)
    setPasteText('')
  }

  const pasteAllCaptions = async () => {
    let text = ''
    try {
      text = await navigator.clipboard.readText()
    } catch {
      // Navegador bloqueou a leitura: abre a janela pra colar com Ctrl+V.
      setPasteText('')
      setPasteOpen(true)
      return
    }
    const list = parseCaptionList(text)
    if (list.length > 0 && list.length === state.items.length) {
      applyCaptionList(list)
      return
    }
    // Contagem diferente (ou vazio): mostra o texto e a contagem antes de aplicar.
    setPasteText(text)
    setPasteOpen(true)
  }

  // Determine grid columns based on what's visible
  const colTemplate = showCaption && showYT
    ? 'grid-cols-[32px_60px_1fr_1fr_1fr_1fr]'   // # | thumb | legenda | titulo YT | desc YT | tags+priv
    : showYT
      ? 'grid-cols-[32px_60px_1fr_1fr_1fr]'       // # | thumb | titulo YT | desc YT | tags+priv
      : 'grid-cols-[32px_60px_1fr]'                // # | thumb | legenda

  return (
    <div className="min-w-0">
      {/* YT shared settings (tags + privacy) — sticky top */}
      {showYT && (
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-[rgba(255,255,255,0.09)] bg-[#1c1c20] px-5 py-2.5">
          <span className="rounded bg-[#ff4444]/10 px-1.5 py-0.5 text-[8px] font-bold text-[#ff4444]">YT</span>
          <span className="text-[10px] text-[#888580]">Tags:</span>
          <Input
            value={state.ytTags}
            onChange={e => dispatch({ type: 'SET_FIELD', field: 'ytTags', value: e.target.value })}
            placeholder="tag1, tag2, tag3..."
            className="h-7 w-48 text-xs bg-transparent border-[rgba(255,255,255,0.09)]"
          />
          <span className="text-[10px] text-[#888580]">Privacidade:</span>
          <Select value={state.ytPrivacy}
            onValueChange={v => dispatch({ type: 'SET_FIELD', field: 'ytPrivacy', value: v })}>
            <SelectTrigger className="h-7 w-28 text-xs bg-transparent border-[rgba(255,255,255,0.09)]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Publico</SelectItem>
              <SelectItem value="unlisted">Nao listado</SelectItem>
              <SelectItem value="private">Privado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Actions bar */}
      <div className={cn('sticky z-10 flex items-center gap-3 border-b border-[rgba(255,255,255,0.09)] bg-[#1c1c20] px-5 py-2',
        showYT ? 'top-[45px]' : 'top-0')}>
        <button type="button" onClick={copyAllFilenames}
          className="flex items-center gap-1 rounded-md border border-[rgba(255,255,255,0.09)] px-2.5 py-1 text-[10px] font-semibold text-[#c9a84c] hover:bg-[#c9a84c]/5">
          <Copy className="h-3 w-3" /> Copiar titulos (nomes)
        </button>
        {showCaption && (
          <button type="button" onClick={copyAllCaptions}
            className="flex items-center gap-1 rounded-md border border-[rgba(255,255,255,0.09)] px-2.5 py-1 text-[10px] font-semibold text-[#c9a84c] hover:bg-[#c9a84c]/5">
            <Copy className="h-3 w-3" /> Copiar legendas
          </button>
        )}
        {showCaption && (
          <button type="button" onClick={pasteAllCaptions} disabled={state.items.length === 0}
            title="Cola uma legenda por linha, na ordem dos videos"
            className="flex items-center gap-1 rounded-md border border-[rgba(255,255,255,0.09)] px-2.5 py-1 text-[10px] font-semibold text-[#c9a84c] hover:bg-[#c9a84c]/5 disabled:opacity-50">
            <ClipboardPaste className="h-3 w-3" /> Colar legendas
          </button>
        )}
        <div className="flex-1" />
        <span className="text-[10px] text-[#888580]">{state.items.length} videos</span>
      </div>

      {/* Table header */}
      <div className={cn('sticky z-[9] grid items-center gap-3 border-b border-[rgba(255,255,255,0.09)] bg-[#222226]/90 backdrop-blur-sm px-5 py-2 text-[10px] font-semibold text-[#888580] uppercase tracking-wider',
        colTemplate, showYT ? 'top-[85px]' : 'top-[37px]')}>
        <span>#</span>
        <span>Thumb</span>
        {showCaption && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span>Legenda</span>
              <span className="rounded bg-[#c9a84c]/10 px-1 py-0.5 text-[7px] font-bold text-[#c9a84c] normal-case">
                {hasIG && hasFB ? 'IG+FB' : hasIG ? 'IG' : 'FB'}
              </span>
            </div>
            {state.items.length > 1 && (
              <button type="button" onClick={() => applyFirstToAll('caption')}
                className="flex items-center gap-0.5 text-[10px] text-[#c9a84c] hover:underline normal-case font-semibold">
                <Copy className="h-2.5 w-2.5" /> 1o p/ todos
              </button>
            )}
          </div>
        )}
        {showYT && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span>Titulo YT</span>
                <span className="rounded bg-[#ff4444]/10 px-1 py-0.5 text-[7px] font-bold text-[#ff4444] normal-case">YT</span>
              </div>
              {state.items.length > 1 && (
                <button type="button" onClick={() => applyFirstToAll('ytTitle')}
                  className="flex items-center gap-0.5 text-[10px] text-[#c9a84c] hover:underline normal-case font-semibold">
                  <Copy className="h-2.5 w-2.5" /> 1o p/ todos
                </button>
              )}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span>Descricao YT</span>
                <span className="rounded bg-[#ff4444]/10 px-1 py-0.5 text-[7px] font-bold text-[#ff4444] normal-case">YT</span>
              </div>
              {state.items.length > 1 && (
                <button type="button" onClick={() => applyFirstToAll('ytDescription')}
                  className="flex items-center gap-0.5 text-[10px] text-[#c9a84c] hover:underline normal-case font-semibold">
                  <Copy className="h-2.5 w-2.5" /> 1o p/ todos
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Table rows — ALL items visible */}
      {state.items.map((item, idx) => (
        <div key={item.id}
          className={cn('grid items-start gap-3 border-b border-[rgba(255,255,255,0.09)] px-5 py-3',
            colTemplate, idx % 2 === 0 && 'bg-[#1a1a1e]')}>
          {/* # */}
          <span className="pt-2 text-xs font-semibold text-[#888580]">{idx + 1}</span>

          {/* Thumbnail */}
          <div className="relative h-16 w-14 shrink-0 overflow-hidden rounded-lg border border-[rgba(255,255,255,0.09)] bg-[#222226]">
            {item.mediaType === 'video'
              ? <video src={item.url} className="h-full w-full object-cover" muted />
              : <img src={item.url} alt="" className="h-full w-full object-cover" />}
          </div>

          {/* Caption */}
          {showCaption && (
            <div className="relative">
              <Textarea
                value={getCaption(item.id)}
                onChange={e => updateItemField(item.id, 'caption', e.target.value)}
                placeholder="Legenda do post..."
                className="min-h-[64px] resize-none text-xs bg-[#151518] border-[rgba(255,255,255,0.09)] pr-8"
              />
              {aiStatus?.connected && (
                <button type="button" onClick={() => handleAI(item.id)}
                  disabled={generateCaption.isPending}
                  title="Gerar legenda com IA"
                  className="absolute right-1.5 top-1.5 rounded p-1 text-[#888580] hover:bg-[#c9a84c]/10 hover:text-[#c9a84c] disabled:opacity-50">
                  {generateCaption.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                </button>
              )}
            </div>
          )}

          {/* YT Title */}
          {showYT && (
            <Input
              value={getYtTitle(item.id)}
              onChange={e => updateItemField(item.id, 'ytTitle', e.target.value)}
              placeholder="Titulo YouTube..."
              className="h-9 text-xs bg-[#151518] border-[rgba(255,255,255,0.09)]"
            />
          )}

          {/* YT Description */}
          {showYT && (
            <Textarea
              value={getYtDesc(item.id)}
              onChange={e => updateItemField(item.id, 'ytDescription', e.target.value)}
              placeholder="Descricao YouTube..."
              className="min-h-[64px] resize-none text-xs bg-[#151518] border-[rgba(255,255,255,0.09)]"
            />
          )}
        </div>
      ))}

      {/* No platforms */}
      {!showCaption && !showYT && (
        <div className="flex items-center justify-center py-16 text-sm text-[#888580]">
          Nenhuma plataforma selecionada. Volte ao passo anterior.
        </div>
      )}

      {/* Colar legendas: so aparece quando a contagem nao bate ou o navegador bloqueou a leitura */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Colar legendas</DialogTitle>
            <DialogDescription>Uma legenda por linha, na ordem dos videos. Linhas vazias sao ignoradas.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={pasteText}
            onChange={e => setPasteText(e.target.value)}
            placeholder="Cole aqui (Ctrl+V)..."
            className="min-h-[220px] text-xs bg-[#151518] border-[rgba(255,255,255,0.09)]"
          />
          <div className={cn('text-[11px]', pasteCount === state.items.length ? 'text-[#888580]' : 'text-[#ff4444]')}>
            {pasteCount} legendas detectadas · {state.items.length} videos
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasteOpen(false)}>Cancelar</Button>
            <Button onClick={() => applyCaptionList(parseCaptionList(pasteText))} disabled={pasteCount === 0}>
              {pasteCount > 0 && pasteCount < state.items.length ? `Aplicar nas ${pasteCount} primeiras` : 'Aplicar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
